import { Edit01, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { DATE_FORMAT, USER_ROLE } from 'src/constants/common';
import { capitalizeFirstLetter, getShortName, onError } from 'src/utils/CommonFunctions';

interface IProps {
    data: any;
    handleActionType: (action: any, mode?: any, team?: any) => () => void;
    resentEmailInvitation: any;
    currentUser: any;
    selectedMemberIds?: number[];
    onToggleSelect?: (id: number) => void;
}

const MemberRow = ({ data, handleActionType, currentUser, selectedMemberIds, onToggleSelect }: IProps) => {
    const { t } = useTranslation();
    const teamMemberNameRef = useRef<HTMLHeadingElement>(null);
    const emailRef = useRef<HTMLSpanElement>(null);

    const getUserAccountTypeColor = (accountType: string) => {
        const normalizedType = accountType?.toLowerCase();

        if (normalizedType === USER_ROLE.ADMIN.toLowerCase()) {
            return 'success';
        } else if (normalizedType === USER_ROLE.USER.toLowerCase()) {
            return 'info';
        }
    };

    const image = data?.imageUrl ? data?.imageUrl + data?.image : data?.image;

    return (
        <>
            <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                <CheckBox
                    id={`select-member-${data.id}`}
                    checked={!!selectedMemberIds?.includes(data.id)}
                    onChangeFunc={(e: any) => {
                        e.stopPropagation();
                        onToggleSelect?.(data.id);
                    }}
                    name="selectMember"
                    value={String(data.id)}
                    parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                />
            </td>
            <td className="max-w-px min-w-[240px]" onClick={handleActionType('team_management', 'view_profile', data)}>
                <div className="flex items-center">
                    <div className="table-profile-wrap notification-mark-wrap">
                        {image ? (
                            <img className="table-profile-icn-circle-xs" src={image} alt={data?.name || `userImgAlt`} title={data?.name || `userImg`} onError={onError} />
                        ) : (
                            <div className="table-profile-customer-circle-xs">{getShortName((data?.firstName && data?.lastName && `${data?.firstName} ${data?.lastName}`) || data?.email)}</div>
                        )}

                        {data?.hasUnreadNotification ? <span className={`notification-dot`}></span> : ''}
                    </div>
                    <div className="ml-2 truncate">
                        <TruncateTextCmp refName={teamMemberNameRef} tooltipType="action" message={`${(data?.firstName && data?.lastName && `${data?.firstName} ${data?.lastName}`) || data?.email}`}>
                            <h6 className="text-grayLight900 text-[12px] leading-[18px] block font-medium truncate" ref={teamMemberNameRef}>{`${
                                (data?.firstName && data?.lastName && `${data?.firstName} ${data?.lastName}`) || data?.email
                            }`}</h6>
                        </TruncateTextCmp>
                    </div>
                </div>
            </td>
            <td className="max-w-[9.15%] min-w-[140px]">
                <div className="flex">
                    {data?.role ? (
                        <BadgeCmp style="modern" type={getUserAccountTypeColor(data?.role?.toUpperCase())} mainClassName="me-2">
                            {capitalizeFirstLetter(data?.role?.toLowerCase())}
                        </BadgeCmp>
                    ) : (
                        <span className="text-grayLight600 text-[12px] leading-[18px] block font-normal truncate">-</span>
                    )}
                </div>
            </td>
            <td className="max-w-px min-w-[210px]">
                <div className="flex">
                    <TruncateTextCmp refName={emailRef} tooltipType="action" message={data?.email || '-'}>
                        <span ref={emailRef} className="text-grayLight600 text-[12px] leading-[18px] block font-normal truncate">
                            {data?.email ? data?.email : '-'}
                        </span>
                    </TruncateTextCmp>
                </div>
            </td>
            <td className="max-w-[9.15%] min-w-[200px]">
                <span>{data.lastLoginAt ? moment(data.lastLoginAt).format(DATE_FORMAT) : '-'}</span>
            </td>
            <td className="max-w-[9.15%] min-w-[170px]">
                {data?.isActive ? (
                    <BadgeCmp style="modern" type="success">
                        Active
                    </BadgeCmp>
                ) : (
                    <BadgeCmp style="modern" type="warning">
                        Pending
                    </BadgeCmp>
                )}
            </td>
            <td className="w-[64px] min-w-[64px] max-w-[64px] sticky right-0 bg-inherit z-[1] table-cell-with-tooltip">
                <div className="flex items-center justify-end gap-1.5 h-full table-col-tooltip-space-common-xs">
                    {!(data?.id === currentUser?.id) && (
                        <>
                            <TooltipCmp message={t('Common.Edit')} position="top">
                                <div className="w-4 h-4 flex items-center justify-center flex-none cursor-pointer" onClick={handleActionType('team', 'member-update', data)}>
                                    <Edit01 className="w-4 h-4 text-grayLight600" />
                                </div>
                            </TooltipCmp>
                            <TooltipCmp message={t('Common.Delete')} position="top">
                                <div className="w-4 h-4 flex items-center justify-center flex-none cursor-pointer" onClick={handleActionType('team', 'member-delete', data)}>
                                    <Trash01 className="w-4 h-4 text-grayLight600" />
                                </div>
                            </TooltipCmp>
                        </>
                    )}
                </div>
            </td>
        </>
    );
};

export default MemberRow;
